/**
 * Shared marking behaviour for the two mark pages: the teacher's Grid and the
 * base commander's Task rows. Both mark the same endpoint the same way, so the
 * in-flight handling lives here rather than being written twice.
 */

// how long a typed quantity sits before it is sent, so a marker can finish
// entering the number instead of firing a request off every digit
export const SAVE_DELAY = 2000;

/**
 * Sends one mark at a time per cell.
 * 
 * Marking is a DELETE then an INSERT server side, so two requests for the same
 * cell in flight together can interleave and settle out of order - the dbs
 * would end up holding the earlier click rather than the later one. Anything
 * marked while a mark is saving is held here instead, replacing whatever else
 * was waiting, so only the newest value is ever sent on.
 */
export class MarkQueue {

  // notify( key, saving ) fires whenever a cell starts or stops saving
  constructor( notify ) {
    this.notify = notify;
    this.inflight = {};
    this.queued = {};
  }

  // stop talking to a component that is going away
  stop() {
    this.notify = null;
  }

  ping( key, saving ) {
    if ( this.notify ) this.notify( key, saving );
  }

  /**
   * `request` is a thunk, not a promise: a held mark is built when it runs,
   * off the store as it is by then, not as it was at the time of the click.
   */
  run( key, request ) {
    if ( this.inflight[ key ] ) {
      this.queued[ key ] = request;
      return Promise.resolve();
    }

    this.inflight[ key ] = true;
    this.ping( key, true );

    const next = () => {
      delete this.inflight[ key ];

      const waiting = this.queued[ key ];
      if ( waiting ) { // keep spinning, the cell is still not settled
        delete this.queued[ key ];
        return this.run( key, waiting );
      }

      this.ping( key, false );
    };
    // errors are surfaced by the page above us; here we only stop spinning
    return Promise.resolve( request() ).then( next, next );
  }
}

/**
 * Holds a typed quantity back until the typing stops, per cell.
 */
export class MarkDebouncer {

  // notify( key, waiting ) fires whenever a cell starts or stops holding a value
  constructor( notify, delay = SAVE_DELAY ) {
    this.notify = notify;
    this.delay = delay;
    this.timers = {};
    this.values = {};
    this.sends = {};
  }

  // send anything still held, without touching a component that is going away
  stop() {
    this.notify = null;
    this.flushAll();
  }

  ping( key, waiting ) {
    if ( this.notify ) this.notify( key, waiting );
  }

  clear( key ) {
    if ( this.timers[ key ] ) {
      clearTimeout( this.timers[ key ] );
      delete this.timers[ key ];
    }
  }

  hold( key, value, send ) {
    this.clear( key );

    this.values[ key ] = value;
    this.sends[ key ] = send;
    this.timers[ key ] = setTimeout( () => this.flush( key ), this.delay );

    this.ping( key, true );
  }

  // send a held value now; a no-op if this cell has nothing held
  flush( key ) {
    if ( !this.timers[ key ] ) return;

    this.clear( key );

    const value = this.values[ key ];
    const send = this.sends[ key ];
    delete this.values[ key ];
    delete this.sends[ key ];

    this.ping( key, false );
    send( value );
  }

  flushAll() {
    Object.keys( this.timers ).forEach( key => this.flush( key ) );
  }
}



// WEBPACK FOOTER //
// ./src/components/missions/marking.js